import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Download, ExternalLink } from "lucide-react";

/**
 * Internal brand bible viewer. Not linked from the public site.
 * Reachable at /brand. Renders /brand/BRAND_GUIDE.md with typographic care.
 * Asset files live under /brand/assets/ (also served statically).
 */
export default function Brand() {
    const [md, setMd] = useState("");
    const [err, setErr] = useState(null);

    useEffect(() => {
        document.title = "Cadence — Brand guide";
        fetch("/brand/BRAND_GUIDE.md")
            .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then(setMd)
            .catch(setErr);
    }, []);

    return (
        <main className="min-h-screen bg-[#F6F5F2] text-[#1A1A1A]" data-testid="brand-page">
            <header className="sticky top-0 z-40 border-b border-black/5 bg-[#F6F5F2]/80 backdrop-blur-xl">
                <div className="max-w-4xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
                    <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900">
                        <ArrowLeft size={16} strokeWidth={1.5} /> Back to site
                    </Link>
                    <div className="flex items-center gap-3">
                        <a
                            href="/brand/cadence-brand-kit.zip"
                            download
                            className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F6F5F2] rounded-full px-4 py-2 text-sm font-medium hover:bg-terracotta transition-colors"
                            data-testid="brand-download-zip"
                        >
                            <Download size={14} strokeWidth={1.8} /> Download kit
                        </a>
                        <a
                            href="/brand/BRAND_GUIDE.md"
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900"
                            data-testid="brand-view-raw"
                        >
                            Raw <ExternalLink size={13} strokeWidth={1.8} />
                        </a>
                    </div>
                </div>
            </header>

            <div className="max-w-3xl mx-auto px-6 sm:px-10 pt-12 pb-24">
                <div className="text-[11px] tracking-[0.28em] uppercase font-semibold text-terracotta mb-4">
                    Internal · Brand bible v1.0
                </div>

                {err && (
                    <div className="text-sm text-stone-600 border border-black/10 rounded-xl p-6">
                        Couldn&rsquo;t load the guide: {String(err.message || err)}
                        <br />
                        Try the raw file at <a className="underline" href="/brand/BRAND_GUIDE.md">/brand/BRAND_GUIDE.md</a>.
                    </div>
                )}

                <article className="brand-prose">
                    <ReactMarkdown>{md}</ReactMarkdown>
                </article>

                <div className="mt-16 pt-8 border-t border-black/10">
                    <div className="text-[11px] tracking-[0.22em] uppercase text-stone-400 font-semibold mb-3">
                        Quick asset links
                    </div>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                        {[
                            ["Full kit (zip)", "/brand/cadence-brand-kit.zip"],
                            ["Brand guide (markdown)", "/brand/BRAND_GUIDE.md"],
                            ["Logo icon — 1024 PNG", "/brand/assets/logo-icon-1024.png"],
                            ["Logo icon — SVG", "/brand/assets/logo-icon.svg"],
                            ["Horizontal lockup — SVG", "/brand/assets/logo-horizontal.svg"],
                            ["Wordmark — SVG", "/brand/assets/wordmark.svg"],
                            ["Open Graph card — 2400×1260 PNG", "/brand/assets/og-card@2x.png"],
                            ["Colors — colors.css", "/brand/assets/colors.css"],
                            ["Typography — type.css", "/brand/assets/type.css"],
                            ["Android adaptive 432 PNG", "/brand/assets/logo-icon-android-432.png"],
                            ["iOS app icon 1024 PNG", "/brand/assets/logo-icon-1024.png"],
                            ["Partnership one-pager", "/brand/assets/one-pager-template.md"],
                        ].map(([label, href]) => (
                            <li key={href}>
                                <a
                                    href={href}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1.5 text-stone-700 hover:text-terracotta"
                                >
                                    {label} <ExternalLink size={12} strokeWidth={1.8} />
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </main>
    );
}
